import type { ChatPanelRecipientPayload } from '../../types/krybot';
import type { ChatThread } from '../../contexts/ChatContext';

export interface PersonDeliveryMetadata {
  deliveryMode: 'person_deliver';
  recipients: ChatPanelRecipientPayload[];
}

function normalizeRecipient(raw: unknown): ChatPanelRecipientPayload | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const id = typeof row.id === 'string' ? row.id : null;
  if (!id) return null;
  const kind = typeof row.kind === 'string' ? row.kind : 'person';
  const label = typeof row.label === 'string' ? row.label : undefined;
  return { kind, id, label };
}

function normalizeRecipientList(raw: unknown): ChatPanelRecipientPayload[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeRecipient).filter((r): r is ChatPanelRecipientPayload => Boolean(r));
}

/** Read person-delivery metadata from a Krybot message row. */
export function readPersonDeliveryMetadata(
  metadata?: Record<string, unknown> | null
): PersonDeliveryMetadata | null {
  if (!metadata || typeof metadata !== 'object') return null;
  const mode = metadata.delivery_mode ?? metadata.deliveryMode;
  if (mode !== 'person_deliver') return null;
  const recipients = normalizeRecipientList(
    metadata.chat_panel_recipients ?? metadata.chatPanelRecipients
  );
  if (recipients.length === 0) return null;
  return { deliveryMode: 'person_deliver', recipients };
}

/** Format recipient labels for bubble headers (e.g. "You → Alice, Bob"). */
export function formatPersonRecipientLabels(recipients: ChatPanelRecipientPayload[]): string {
  return recipients
    .map((r) => r.label?.trim() || r.id)
    .filter(Boolean)
    .join(', ');
}

/** Map stored thread recipients to RecipientPicker values. */
export function recipientsToPickerValues(recipients: ChatPanelRecipientPayload[]): string[] {
  return recipients.map((r) => `${r.kind}:${r.id}`);
}

/** Read chatPanelRecipients from a thread's context (artifact_context merged on load). */
export function readThreadChatPanelRecipients(thread: ChatThread): ChatPanelRecipientPayload[] {
  const ctx = thread.context;
  if (!ctx || typeof ctx !== 'object') return [];
  return normalizeRecipientList(ctx.chatPanelRecipients ?? ctx.chat_panel_recipients);
}
